/**
 * IMANUEL display currency — convert storefront prices for viewing only.
 * Cart / checkout always stay in USD (shop currency).
 */
(function () {
  const PREF_KEY = "imanuel_locale_pref_v1";
  const RATES_KEY = "imanuel_fx_rates_v1";
  /** Soft cache — still refresh in the background before this expires. */
  const RATES_TTL_MS = 60 * 60 * 1000;
  /** Always hit the network again if older than this when switching currency. */
  const RATES_STALE_MS = 15 * 60 * 1000;

  const SYMBOLS = {
    USD: "$",
    GBP: "£",
    EUR: "€",
    AED: "AED ",
    AUD: "A$",
    CAD: "CA$",
    CHF: "CHF ",
    HKD: "HK$",
    JPY: "¥",
    NZD: "NZ$",
    SGD: "S$",
  };

  /** Leaf nodes that hold a single money amount (never whole product-price blocks). */
  const AMOUNT_SELECTOR = [
    "span.price",
    "span.compare-at-price",
    ".price-item--sale",
    ".price-item--regular",
    ".product__price",
    "[data-bs-price]",
    ".cart-item__top > p:last-child",
    "#bundle-price",
    "#bundle-compare",
    "#bundle-save",
    "#bundle-each",
    "#qa-price",
    ".sticky-atc__info small",
    "[data-cart-subtotal]",
    "[data-cart-total]",
    "[data-usd-cents]",
  ].join(",");

  /** @type {Record<string, number>} */
  let rates = { USD: 1 };
  /** When the FX provider last published these market rates (often once daily). */
  let ratesAsOf = 0;
  /** When this browser last fetched rates from the network. */
  let ratesFetchedAt = 0;
  let currency = "USD";
  let country = "US";
  let ready = false;
  /** @type {Promise<Record<string, number>> | null} */
  let ratesPromise = null;

  function readPref() {
    try {
      const raw = localStorage.getItem(PREF_KEY);
      if (!raw) return null;
      return JSON.parse(raw);
    } catch (_) {
      return null;
    }
  }

  function writePref(next) {
    try {
      localStorage.setItem(PREF_KEY, JSON.stringify(next));
    } catch (_) {}
  }

  function loadCachedRates() {
    try {
      const raw = localStorage.getItem(RATES_KEY);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      if (!parsed?.rates || !parsed?.fetchedAt) return null;
      if (Date.now() - parsed.fetchedAt > RATES_TTL_MS) return null;
      return parsed;
    } catch (_) {
      return null;
    }
  }

  function cacheRates(nextRates, asOf, fetchedAt) {
    try {
      localStorage.setItem(
        RATES_KEY,
        JSON.stringify({
          asOf: asOf || fetchedAt || Date.now(),
          fetchedAt: fetchedAt || Date.now(),
          rates: nextRates,
        })
      );
    } catch (_) {}
  }

  function ratesAreStale() {
    return !ratesFetchedAt || Date.now() - ratesFetchedAt > RATES_STALE_MS;
  }

  /**
   * Live FX from public APIs (USD base). Checkout stays USD — these rates are
   * display-only. Tries several providers so a single outage doesn't freeze prices.
   * @param {boolean} [force]
   */
  function fetchRates(force = false) {
    if (!force && ratesPromise) return ratesPromise;

    ratesPromise = (async () => {
      if (!force) {
        const cached = loadCachedRates();
        if (cached) {
          rates = { USD: 1, ...cached.rates };
          ratesAsOf = cached.asOf || cached.fetchedAt;
          ratesFetchedAt = cached.fetchedAt;
          // Refresh quietly in the background if getting stale
          if (ratesAreStale()) {
            ratesPromise = null;
            fetchRates(true).then(() => {
              if (currency !== "USD") convertRoot(document);
              syncCurrencyUi();
            });
          }
          return rates;
        }
      }

      const endpoints = [
        {
          url: "https://open.er-api.com/v6/latest/USD",
          parse: (d) => (d.result === "success" ? d.rates : null),
          updated: (d) => (d.time_last_update_unix ? d.time_last_update_unix * 1000 : Date.now()),
        },
        {
          url: "https://api.frankfurter.app/latest?from=USD",
          parse: (d) => d.rates || null,
          updated: (d) => (d.date ? Date.parse(d.date + "T12:00:00Z") : Date.now()),
        },
        {
          url: "https://cdn.jsdelivr.net/npm/@fawazahmed0/currency-api@latest/v1/currencies/usd.json",
          parse: (d) => {
            const src = d.usd || d.USD;
            if (!src || typeof src !== "object") return null;
            const out = {};
            Object.keys(src).forEach((k) => {
              out[k.toUpperCase()] = Number(src[k]);
            });
            return out;
          },
          updated: (d) => (d.date ? Date.parse(d.date + "T12:00:00Z") : Date.now()),
        },
      ];

      for (const ep of endpoints) {
        try {
          const res = await fetch(ep.url, { cache: "no-store" });
          if (!res.ok) continue;
          const data = await res.json();
          const map = ep.parse(data);
          if (!map || typeof map !== "object" || (!map.GBP && !map.EUR)) continue;
          rates = { USD: 1, ...map };
          ratesFetchedAt = Date.now();
          ratesAsOf = ep.updated(data) || ratesFetchedAt;
          cacheRates(map, ratesAsOf, ratesFetchedAt);
          return rates;
        } catch (_) {}
      }

      // Keep last good in-memory rates if network failed
      if (Object.keys(rates).length > 1) return rates;
      rates = { USD: 1 };
      ratesAsOf = 0;
      ratesFetchedAt = 0;
      ratesPromise = null;
      return rates;
    })();

    return ratesPromise;
  }

  function rateFor(code) {
    const c = String(code || "USD").toUpperCase();
    if (c === "USD") return 1;
    const r = rates[c];
    return typeof r === "number" && r > 0 ? r : 1;
  }

  function formatConverted(usdAmount, code = currency) {
    const c = String(code || "USD").toUpperCase();
    const amount = Number(usdAmount) * rateFor(c);
    const digits = c === "JPY" ? 0 : 2;
    const formatted = amount.toLocaleString("en-US", {
      minimumFractionDigits: digits,
      maximumFractionDigits: digits,
    });
    const sym = SYMBOLS[c] || `${c} `;
    return `${sym}${formatted}`;
  }

  function formatUsdCents(cents, code = currency) {
    return formatConverted(Number(cents) / 100, code);
  }

  function formatUsdAmount(n, code = currency) {
    return formatConverted(Number(n), code);
  }

  function applyPreference(pref) {
    if (!pref) return;
    if (pref.currency) currency = String(pref.currency).toUpperCase();
    if (pref.code) country = String(pref.code).toUpperCase();
    document.documentElement.dataset.displayCurrency = currency;
    document.documentElement.dataset.displayCountry = country;
  }

  function parseUsdNumber(text) {
    if (!text) return null;
    const cleaned = String(text)
      .replace(/SAVE\s*/i, "")
      .replace(/[^\d.,-]/g, "")
      .replace(/,/g, "");
    if (!cleaned || cleaned === "-" || cleaned === ".") return null;
    const n = Number(cleaned);
    return Number.isFinite(n) ? n : null;
  }

  function isAmountLeaf(el) {
    if (!(el instanceof HTMLElement)) return false;
    if (el.closest("imanuel-locale-popup, script, style, noscript, .ics, .ics-sheet")) return false;
    if (el.dataset.skipFx === "true") return false;
    // Reject wrappers that contain a11y labels or nested money nodes
    if (el.matches("product-price, .price-list, .price__container, .price__regular, .price__sale, .price-item__group")) {
      return false;
    }
    if (el.querySelector("span.price, .compare-at-price, .visually-hidden, product-price")) return false;
    return true;
  }

  function captureOriginal(el) {
    if (el.dataset.usdText) return;
    const raw = (el.textContent || "").trim();
    if (!raw || !/[\d]/.test(raw)) return;
    if (/sale price|regular price|price from/i.test(raw)) return;
    el.dataset.usdText = raw;
    if (el.dataset.usdCents == null || el.dataset.usdCents === "") {
      const n = parseUsdNumber(raw);
      if (n != null) el.dataset.usdAmount = String(n);
    }
  }

  function applyToElement(el) {
    if (!isAmountLeaf(el)) return;

    if (el.dataset.usdCents != null && el.dataset.usdCents !== "") {
      const cents = Number(el.dataset.usdCents);
      if (!Number.isFinite(cents)) return;
      if (!el.dataset.usdText) el.dataset.usdText = (el.textContent || "").trim();
      el.textContent = currency === "USD" && el.dataset.usdText
        ? el.dataset.usdText
        : formatUsdCents(cents);
      el.dataset.fxApplied = currency;
      return;
    }

    captureOriginal(el);
    if (!el.dataset.usdText) return;

    if (currency === "USD") {
      el.textContent = el.dataset.usdText;
      el.dataset.fxApplied = "USD";
      return;
    }

    const amount = el.dataset.usdAmount != null
      ? Number(el.dataset.usdAmount)
      : parseUsdNumber(el.dataset.usdText);
    if (amount == null || !Number.isFinite(amount)) return;

    const prefix = /^\s*SAVE\s+/i.test(el.dataset.usdText) ? "SAVE " : "";
    const suffixMatch = el.dataset.usdText.match(/\s+(each(?:\s*\([^)]*\))?)\s*$/i);
    const suffix = suffixMatch ? ` ${suffixMatch[1]}` : "";
    el.textContent = prefix + formatUsdAmount(amount) + suffix;
    el.dataset.fxApplied = currency;
  }

  function collectAmountNodes(root = document) {
    const set = new Set();
    root.querySelectorAll?.(AMOUNT_SELECTOR)?.forEach((el) => {
      if (isAmountLeaf(el)) set.add(el);
    });
    root.querySelectorAll?.("[data-usd-text], [data-usd-cents]")?.forEach((el) => {
      if (isAmountLeaf(el)) set.add(el);
    });
    return [...set];
  }

  function convertRoot(root = document) {
    if (!ready) return;
    collectAmountNodes(root).forEach(applyToElement);
  }

  async function setDisplayCurrency(code, countryCode) {
    currency = String(code || "USD").toUpperCase();
    if (countryCode) country = String(countryCode).toUpperCase();
    writePref({ code: country, currency, at: Date.now() });
    applyPreference({ code: country, currency });

    // Pull live FX when switching (force if cache is stale)
    await fetchRates(ratesAreStale());
    ready = true;
    convertRoot(document);
    syncCurrencyUi();

    window.dispatchEvent(
      new CustomEvent("imanuel:display-currency", { detail: { currency, country, rates } })
    );
  }

  const DISPLAY_CURRENCIES = [
    { code: "USD", name: "US Dollar", symbol: "$", country: "US" },
    { code: "GBP", name: "British Pound", symbol: "£", country: "GB" },
    { code: "EUR", name: "Euro", symbol: "€", country: "DE" },
    { code: "AED", name: "UAE Dirham", symbol: "د.إ", country: "AE" },
    { code: "CAD", name: "Canadian Dollar", symbol: "CA$", country: "CA" },
    { code: "AUD", name: "Australian Dollar", symbol: "A$", country: "AU" },
    { code: "CHF", name: "Swiss Franc", symbol: "CHF", country: "CH" },
    { code: "HKD", name: "Hong Kong Dollar", symbol: "HK$", country: "HK" },
    { code: "JPY", name: "Japanese Yen", symbol: "¥", country: "JP" },
    { code: "SGD", name: "Singapore Dollar", symbol: "S$", country: "SG" },
    { code: "NZD", name: "New Zealand Dollar", symbol: "NZ$", country: "NZ" },
  ];

  function formatRateHint(code) {
    const c = String(code || "USD").toUpperCase();
    if (c === "USD") return "1:1";
    const r = rateFor(c);
    if (!r || r === 1 && c !== "USD" && !rates[c]) return "—";
    return r.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 4 });
  }

  function formatRatesUpdatedLabel() {
    if (!ratesAsOf && !ratesFetchedAt) return "Fetching live rates…";
    try {
      // Free FX APIs usually publish once per day — show market "as of", not fetch time.
      const when = new Date(ratesAsOf || ratesFetchedAt);
      return (
        "Live FX · market rates as of " +
        when.toLocaleString(undefined, {
          month: "short",
          day: "numeric",
          hour: "2-digit",
          minute: "2-digit",
        })
      );
    } catch (_) {
      return "Live FX rates";
    }
  }

  function optionHtml(item, active) {
    const rateLabel = item.code === "USD" ? "Base" : `1 USD = ${formatRateHint(item.code)}`;
    return (
      `<button type="button" class="ics__option${active ? " is-active" : ""}" role="option" aria-selected="${active ? "true" : "false"}" data-ics-currency="${item.code}" data-ics-country="${item.country}">` +
      `<span class="ics__option-sym" aria-hidden="true">${item.symbol}</span>` +
      `<span class="ics__option-body"><span class="ics__option-code">${item.code}</span><span class="ics__option-name">${item.name}</span><span class="ics__option-rate">${rateLabel}</span></span>` +
      `<span class="ics__option-check" aria-hidden="true"><svg width="14" height="14" viewBox="0 0 16 16" fill="none"><path d="M3.2 8.2 6.3 11.2 12.8 4.6" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/></svg></span>` +
      `</button>`
    );
  }

  function syncCurrencyUi() {
    document.querySelectorAll("[data-ics-label]").forEach((el) => {
      el.textContent = currency;
    });
    document.querySelectorAll("[data-ics-list], [data-ics-list-mobile]").forEach((list) => {
      list.innerHTML = DISPLAY_CURRENCIES.map((c) => optionHtml(c, c.code === currency)).join("");
    });
    document.querySelectorAll("[data-ics-rates-note]").forEach((el) => {
      el.textContent = formatRatesUpdatedLabel() + " · Checkout in USD";
    });
  }

  function isMobileCurrencyUi() {
    return window.matchMedia("(max-width: 820px)").matches;
  }

  function closeCurrencyUi() {
    document.querySelectorAll("[data-ics-open]").forEach((btn) => btn.setAttribute("aria-expanded", "false"));
    document.querySelectorAll("[data-ics-dropdown]").forEach((el) => {
      el.hidden = true;
    });
    const sheet = document.querySelector("[data-ics-sheet]");
    if (sheet) {
      sheet.hidden = true;
      document.documentElement.classList.remove("ics-sheet-open");
    }
  }

  function openCurrencyUi() {
    document.querySelectorAll("[data-ics-open]").forEach((btn) => btn.setAttribute("aria-expanded", "true"));
    if (isMobileCurrencyUi()) {
      const sheet = document.querySelector("[data-ics-sheet]");
      if (sheet) {
        sheet.hidden = false;
        document.documentElement.classList.add("ics-sheet-open");
      }
      document.querySelectorAll("[data-ics-dropdown]").forEach((el) => {
        el.hidden = true;
      });
    } else {
      document.querySelectorAll("[data-ics-dropdown]").forEach((el) => {
        el.hidden = false;
      });
    }
    // Refresh live rates whenever the selector opens
    fetchRates(ratesAreStale()).then(() => {
      syncCurrencyUi();
      if (currency !== "USD") convertRoot(document);
    });
  }

  function bindCurrencySelector() {
    const sheet = document.querySelector("[data-ics-sheet]");
    if (sheet && sheet.parentElement !== document.body) {
      document.body.appendChild(sheet);
    }

    syncCurrencyUi();

    document.addEventListener("click", (e) => {
      const t = /** @type {HTMLElement} */ (e.target);
      const openBtn = t.closest?.("[data-ics-open]");
      const pick = t.closest?.("[data-ics-currency]");
      const closeEl = t.closest?.("[data-ics-close]");

      if (openBtn) {
        e.preventDefault();
        e.stopPropagation();
        const expanded = openBtn.getAttribute("aria-expanded") === "true";
        if (expanded) closeCurrencyUi();
        else openCurrencyUi();
        return;
      }

      if (closeEl) {
        e.preventDefault();
        closeCurrencyUi();
        return;
      }

      if (pick) {
        e.preventDefault();
        e.stopPropagation();
        const code = pick.getAttribute("data-ics-currency");
        const countryCode = pick.getAttribute("data-ics-country");
        if (code) {
          // Fire conversion immediately (async rates, but paints as soon as ready)
          setDisplayCurrency(code, countryCode || undefined);
        }
        closeCurrencyUi();
        return;
      }

      if (!t.closest?.("[data-imanuel-currency-selector]") && !t.closest?.("[data-ics-sheet]")) {
        closeCurrencyUi();
      }
    });

    document.addEventListener("keydown", (e) => {
      if (e.key === "Escape") closeCurrencyUi();
    });

    window.addEventListener("imanuel:display-currency", syncCurrencyUi);
  }

  async function init() {
    applyPreference(readPref());
    syncCurrencyUi();
    await fetchRates();
    ready = true;
    convertRoot(document);
    bindCurrencySelector();

    const obs = new MutationObserver((muts) => {
      if (currency === "USD") return;
      for (const m of muts) {
        m.addedNodes.forEach((n) => {
          if (!(n instanceof HTMLElement)) return;
          convertRoot(n);
        });
      }
    });
    obs.observe(document.body, { childList: true, subtree: true });
  }

  window.imanuelDisplayCurrency = {
    get currency() {
      return currency;
    },
    get country() {
      return country;
    },
    get ready() {
      return ready;
    },
    get rates() {
      return { ...rates };
    },
    get ratesUpdatedAt() {
      return ratesAsOf;
    },
    get ratesAsOf() {
      return ratesAsOf;
    },
    get ratesFetchedAt() {
      return ratesFetchedAt;
    },
    currencies: DISPLAY_CURRENCIES,
    formatUsdCents,
    formatUsdAmount,
    setDisplayCurrency,
    convertRoot,
    refreshRates: () => fetchRates(true),
    init,
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => {
      init();
    });
  } else {
    init();
  }
})();
